import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import MailIcon from '@mui/icons-material/Mail';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, Typography, useTheme, Tooltip, Collapse } from '@mui/material';
import Assets from '../../Component/Common/Assets';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useAppSelector } from '../../Redux/app/hooks';
import localStoreUtil from '../../ApiSetUp/localstore.util';

const useStyles = makeStyles()((theme) => ({
    customList: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.info.main,
        flexGrow: 1,
    },
    listItem: {
        marginTop: "15px",
        borderRadius: '10px',
        zIndex: 2,
        transition: 'background-color 0.3s, color 0.3s',
        "& .MuiTypography-root": {
            fontFamily: "Satoshi-Light"
        },
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            '& .MuiListItemIcon-root': {
                color: theme.palette.common.white,
            },
        },
    },
    activeItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '& .MuiListItemIcon-root': {
            color: theme.palette.common.white,
        },
    },
    normalItem: {
        backgroundColor: 'transparent',
        color: theme.palette.bgGray.main,
        '& .MuiListItemIcon-root': {
            color: theme.palette.bgGray.main,
        },
    },
}));

const drawerWidth = 320;
const miniDrawerWidth = 64;

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.info.main,
    ...theme.mixins.toolbar,
}));


const Sidebar = ({ open, handleDrawerToggle, logOut, setOpen, filteredTabs }) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const [openMenus, setOpenMenus] = useState({});

    const handleMenuClick = (name, path, isSubMenu, link) => {
        if (link) {
            window.location.href = "https://spoton.webitotech.com/dashboard";
            return;
        }
        if (!isSubMenu) {
            setOpenMenus((prev) => ({
                [name]: !prev[name]
            }));
        } else {
            setOpenMenus((prev) => ({
                ...prev,
                [name]: !prev[name]
            }));
        }
        if (!open) {
            setOpen(!open)
        }
        if (name != 'Common') {
            navigate(path);
        }
    };

    const renderSubItems = (subItems) => {
        return (
            <List component="div" disablePadding
                sx={{
                    backgroundColor: theme.palette.lightInfo.main,
                    borderBottomLeftRadius: "12px",
                    borderBottomRightRadius: "12px",
                    marginBottom: "10px",
                    ":before": {
                        content: "''",
                        position: "absolute",
                        left: "0px",
                        top: "-10px",
                        height: "20px",
                        backgroundColor: theme.palette.lightInfo.main,
                        zIndex: 1,
                        width: "20px",
                    },
                    ":after": {
                        content: "''",
                        position: "absolute",
                        right: "0px",
                        top: "-10px",
                        height: "20px",
                        backgroundColor: theme.palette.lightInfo.main,
                        zIndex: 1,
                        width: "20px",
                    },
                }}
            >
                <Box sx={{ borderLeft: "1px solid #fff3", marginLeft: "25px" }}>

                    {subItems?.map((subItem) => {
                        const isActive = location.pathname === subItem.path || location.pathname.includes(subItem.path);
                        return (
                            <React.Fragment key={subItem.name}>
                                <ListItem disablePadding sx={{ display: "block" }}>
                                    <ListItemButton
                                        sx={{
                                            paddingLeft: "28px",
                                            position: "relative",
                                            color: isActive ? theme.palette.primary.main : theme.palette.bgGray.main,
                                            "& .MuiTypography-root": {
                                                fontFamily: "Satoshi-Light",
                                            },
                                            "&.MuiListItemButton-root:hover": {
                                                color: theme.palette.primary.main,
                                                backgroundColor: "transparent",
                                            },
                                            ":before": {
                                                content: "''",
                                                position: "absolute",
                                                left: "0px",
                                                height: "0px",
                                                width: "20px",
                                                borderTop: `1px solid #fff3`
                                            },
                                            ":after": isActive
                                                ? {
                                                    content: "''",
                                                    position: "absolute",
                                                    left: "15px",
                                                    width: "6px",
                                                    height: "6px",
                                                    backgroundColor: theme.palette.primary.main,
                                                    borderRadius: "50%"
                                                }
                                                : {}
                                        }}
                                        onClick={() => handleMenuClick(subItem.name, subItem.path, true)}
                                    >
                                        <ListItemText primary={subItem.name} />
                                        {subItem.subData ? (openMenus[subItem.name] ? <RemoveIcon /> : <AddIcon />) : null}
                                    </ListItemButton>
                                    {subItem.subData && (
                                        <Collapse in={openMenus[subItem.name]} timeout="auto" unmountOnExit>
                                            {renderSubItems(subItem.subData)}
                                        </Collapse>
                                    )}
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </Box>
            </List>
        );
    };


    useEffect(() => {
        if (!open) {
            setOpenMenus({})
        }
    }, [!open])

    return (
        <Drawer
            sx={{
                width: open ? drawerWidth : miniDrawerWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '& .MuiDrawer-paper': {
                    width: open ? drawerWidth : miniDrawerWidth,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    overflowX: 'hidden',
                    backgroundColor: theme.palette.info.main,
                },
            }}
            variant="permanent"
            open={open}
        >
            <DrawerHeader sx={{ justifyContent: open ? "space-between" : "center", padding: "10px" }}>
                {open && (
                    <Box display={"flex"} gap={1} alignItems={"center"}>
                        <Assets absolutePath={true} height={"48px"} src={`/assets/img/Logo.svg`} />
                        <Typography fontSize={"20px"} fontWeight={700} fontFamily={"Satoshi-Bold"} color={theme.palette.bgWhite.main}>
                            Spoton Coating
                        </Typography>
                    </Box>
                )}
                <Box onClick={handleDrawerToggle} sx={{ cursor: "pointer", height: "35px", width: "35px", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: theme.palette.lightInfo.main, borderRadius: "10px", border: `1px solid #3d4453` }}>
                    {open ? <ChevronLeftIcon sx={{ color: theme.palette.bgWhite.main }} /> : <ChevronRightIcon sx={{ color: theme.palette.bgWhite.main }} />}
                </Box>
            </DrawerHeader>
            <Box paddingX={"10px"} bgcolor={theme.palette.info.main}>
                <Divider sx={{ borderColor: "#FFFFFF4D" }} />
            </Box>
            <Box sx={{ maxHeight: 'calc(100vh - 158px)', overflow: "auto" }}>
                <List className={classes.customList} sx={{ padding: open ? '15px' : "10px" }}>
                    {filteredTabs?.map((item) => (
                        <React.Fragment key={item.name}>
                            <ListItem
                                disablePadding
                                className={`${classes.listItem} ${item.path === '/' ? location.pathname === item.path ? classes.activeItem : classes.normalItem : location.pathname.includes(item.path) ? classes.activeItem : classes.normalItem}`}
                                sx={{ "&:hover .list-item-icon": { display: "none", }, "&:hover .list-item-icon-active": { display: "block", } }}
                            >
                                <ListItemButton onClick={() => handleMenuClick(item.name, item.path, false, item?.link)} sx={{ justifyContent: open ? 'initial' : 'center' }}>
                                    <ListItemIcon className="list-item-icon" sx={{ minWidth: 0, mr: open ? 3 : "auto", justifyContent: "center", display: location.pathname === item.path ? "none" : "block", }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemIcon className="list-item-icon-active" sx={{ minWidth: 0, mr: open ? 3 : "auto", justifyContent: "center", display: location.pathname === item.path ? "block" : "none", }}>
                                        {item.activeIcon}
                                    </ListItemIcon>
                                    {open &&
                                        <ListItemText primary={item.name} />
                                    }
                                    {(open && item.subData) ? (openMenus[item.name] ? <RemoveIcon /> : <AddIcon />) : null}
                                </ListItemButton>
                            </ListItem>
                            {item.subData && (
                                <Collapse in={openMenus[item.name]} timeout="auto" unmountOnExit>
                                    {renderSubItems(item.subData)}
                                </Collapse>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
            <Box onClick={logOut} sx={{
                cursor: "pointer", position: "fixed", bottom: "0px", width: open ? '270px' : "44px", zIndex: 99, transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
            }} display={"flex"} justifyContent={"space-between"} alignItems={"center"} backgroundColor={theme.palette.lightInfo.main} padding={open ? "15px 25px" : "10px"}>
                {open && <Typography sx={{ color: theme.palette.bgWhite.main, fontSize: "18px" }}>Logout</Typography>}
                <Box sx={{ height: "42px", width: "42px", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#101928", borderRadius: "50%", border: `1px solid #3d4453` }}>
                    <Assets absolutePath={true} src={`/assets/img/Logout.svg`} />
                </Box>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
